import { createContext, ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';

type Options = {
  color: 'light' | 'dark';
  closeOnBackgroundClick: boolean;
};
const defaultOptions: Options = {
  color: 'dark',
  closeOnBackgroundClick: true,
};
export const ModalContext = createContext<{
  close: () => void;
  show: (_c: ReactNode, o?: Partial<Options>) => true; // true for chainability
}>({
  close: () => {
    throw Error('Modal context used without component');
  },
  show: () => {
    throw Error('Modal context used without component');
  },
});

export default function ModalWrapper({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<null | ReactNode>(null);
  const [options, setOptions] = useState<Options>(defaultOptions);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const body = window.document.body;
      if (
        body &&
        (modal !== null) === !body.classList.contains('overflow-hidden')
      ) {
        body.classList[modal !== null ? 'add' : 'remove']('overflow-hidden');
      }
    }
  }, [modal]);
  const close = () => setModal(null);
  const contextValue = {
    close: close,
    show: (component: ReactNode, options?: Partial<Options>): true => {
      if (options) setOptions(Object.assign(defaultOptions, options));
      setModal(component);
      return true;
    },
  };
  return (
    <>
      <ModalContext.Provider value={contextValue}>
        {children}
      </ModalContext.Provider>
      {modal !== null && (
        <div
          className={classNames(
            'z-2000',
            'absolute',
            'inset-0',
            options.color === 'dark'
              ? ['bg-gray-700/80', 'text-gray-100']
              : undefined,
            options.color === 'light'
              ? ['bg-gray-100/80', 'text-gray-700']
              : undefined
          )}
          onClick={() => options.closeOnBackgroundClick && close()}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {modal}
          </div>
        </div>
      )}
    </>
  );
}
